import React from 'react';
import { Link } from 'react-router-dom';

const Section = ({ title, children }) => (
  <section className="mb-8" data-testid={`privacy-section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <h2 className="text-lg md:text-xl font-semibold text-slate-900 mb-3 border-l-4 border-amber-600 pl-3">
      {title}
    </h2>
    <div className="text-sm md:text-base text-slate-700 leading-relaxed space-y-3">
      {children}
    </div>
  </section>
);

export default function PrivacyPolicy() {
  const updatedAt = '29 de setembro de 2026';

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="privacy-policy-page">
      <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-sm border border-slate-200 p-6 md:p-10">
        {/* Cabeçalho */}
        <header className="mb-8 pb-6 border-b border-slate-200">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-md bg-amber-600 flex items-center justify-center text-white font-bold">
              SG
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-500">Serraria Guedes</p>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Política de Privacidade</h1>
            </div>
          </div>
          <p className="text-sm text-slate-500 mt-3">
            Última atualização: <span className="font-medium text-slate-700">{updatedAt}</span>
          </p>
        </header>

        <p className="text-sm md:text-base text-slate-700 leading-relaxed mb-8">
          A <strong>Serraria Guedes</strong> (CNPJ 20.295.391/0001-26) valoriza a privacidade e a
          segurança dos dados de seus clientes, fornecedores e parceiros. Esta Política descreve
          como coletamos, usamos, armazenamos e protegemos suas informações pessoais quando você
          interage conosco, inclusive por meio do <strong>WhatsApp Business</strong>, em conformidade
          com a Lei Geral de Proteção de Dados (Lei nº 13.709/2018 – LGPD).
        </p>

        <Section title="1. Controlador dos Dados">
          <p>
            O controlador responsável pelo tratamento dos seus dados pessoais é:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Razão Social:</strong> Serraria Guedes</li>
            <li><strong>CNPJ:</strong> 20.295.391/0001-26</li>
            <li><strong>Endereço:</strong> Av. Oatacílio Lira Cabral, 57 — em frente ao Posto Frei Damião</li>
            <li><strong>Contato:</strong> (83) 98781-6165 / (83) 98899-8557</li>
          </ul>
        </Section>

        <Section title="2. Dados que Coletamos">
          <p>Podemos coletar os seguintes dados pessoais para atender às suas solicitações:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Nome completo, CPF ou CNPJ, endereço de entrega e cobrança</li>
            <li>Telefone (incluindo número de WhatsApp) e e-mail</li>
            <li>Conteúdo das mensagens trocadas conosco pelo WhatsApp Business (texto, imagens, áudios, documentos)</li>
            <li>Dados de pedidos, orçamentos, pagamentos e histórico de atendimento</li>
            <li>Dados técnicos de sessão quando você acessa nossos canais digitais (data/hora, IP, dispositivo)</li>
          </ul>
        </Section>

        <Section title="3. Como Usamos seus Dados (Finalidades)">
          <p>Utilizamos seus dados exclusivamente para:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Prestar atendimento comercial, técnico e pós-venda (inclusive automatizado por IA quando aplicável)</li>
            <li>Emitir orçamentos, pedidos, notas fiscais e efetuar entregas</li>
            <li>Enviar notificações operacionais sobre o andamento do seu pedido</li>
            <li>Cumprir obrigações legais, fiscais e regulatórias</li>
            <li>Prevenir fraudes e garantir a segurança das nossas operações</li>
            <li>Melhorar a qualidade dos nossos produtos e serviços</li>
          </ul>
          <p>
            <strong>Não utilizamos</strong> seus dados para envio de marketing sem seu consentimento
            prévio nem os vendemos a terceiros.
          </p>
        </Section>

        <Section title="4. WhatsApp Business e Meta">
          <p>
            Utilizamos a <strong>API oficial do WhatsApp Business</strong>, fornecida pela Meta
            Platforms Inc., para conversar com você. Ao iniciar uma conversa conosco via WhatsApp,
            você concorda que:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              O conteúdo das mensagens transita pela infraestrutura da Meta, sujeito à
              <a
                href="https://www.whatsapp.com/legal/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber-700 underline ml-1"
              >Política de Privacidade do WhatsApp</a>.
            </li>
            <li>
              Armazenamos o histórico das conversas em nossos servidores para permitir continuidade
              do atendimento e cumprir obrigações legais.
            </li>
            <li>
              Podemos utilizar sistemas de <strong>Inteligência Artificial</strong> para gerar
              respostas automáticas, sempre com opção de transferência para atendimento humano.
            </li>
          </ul>
        </Section>

        <Section title="5. Compartilhamento com Terceiros">
          <p>
            Compartilhamos seus dados apenas com prestadores de serviço necessários à operação
            (por exemplo: emissores de nota fiscal, transportadoras, meios de pagamento,
            provedores de infraestrutura em nuvem e a própria Meta/WhatsApp), sempre sob obrigação
            contratual de confidencialidade e limitados ao mínimo necessário para a finalidade.
          </p>
          <p>
            Poderemos divulgar dados quando exigido por lei, ordem judicial ou autoridade
            competente.
          </p>
        </Section>

        <Section title="6. Retenção dos Dados">
          <p>
            Mantemos seus dados pessoais pelo tempo necessário para cumprir as finalidades
            descritas nesta Política e para atender obrigações legais (por exemplo, obrigações
            fiscais e contábeis exigem retenção por até <strong>5 anos</strong> após a última
            operação). Findo esse prazo, os dados são anonimizados ou eliminados.
          </p>
        </Section>

        <Section title="7. Segurança">
          <p>
            Adotamos medidas técnicas e organizacionais razoáveis para proteger seus dados contra
            acessos não autorizados, perda, alteração ou divulgação indevida — incluindo criptografia
            em trânsito (HTTPS/TLS), controles de acesso baseados em função e monitoramento
            contínuo dos sistemas.
          </p>
        </Section>

        <Section title="8. Seus Direitos (LGPD)">
          <p>Você pode, a qualquer momento, solicitar gratuitamente:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Confirmação da existência de tratamento dos seus dados</li>
            <li>Acesso, correção ou atualização de dados incompletos ou desatualizados</li>
            <li>Anonimização, bloqueio ou eliminação de dados desnecessários ou tratados em desconformidade</li>
            <li>Portabilidade dos dados a outro fornecedor</li>
            <li>Revogação do consentimento e informações sobre com quem seus dados foram compartilhados</li>
          </ul>
          <p>
            Para exercer seus direitos, entre em contato pelos canais listados na seção 10 desta
            Política.
          </p>
        </Section>

        <Section title="9. Exclusão de Dados">
          <p>
            Para solicitar a <strong>exclusão dos seus dados pessoais</strong> — inclusive o
            histórico de conversas do WhatsApp — envie um pedido para o WhatsApp
            <strong> (83) 98899-8557</strong> ou para o e-mail de contato listado abaixo,
            informando o número/telefone associado à sua conta. Responderemos em até
            <strong> 15 dias corridos</strong>. Dados que precisamos manter por obrigação legal
            (ex.: notas fiscais) permanecerão retidos apenas pelo prazo estritamente exigido.
          </p>
        </Section>

        <Section title="10. Contato do Encarregado (DPO)">
          <p>Para dúvidas, reclamações ou solicitações sobre esta Política:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>WhatsApp:</strong> (83) 98899-8557</li>
            <li><strong>Telefone:</strong> (83) 98781-6165</li>
            <li><strong>Endereço:</strong> Av. Oatacílio Lira Cabral, 57 — em frente ao Posto Frei Damião</li>
          </ul>
        </Section>

        <Section title="11. Alterações desta Política">
          <p>
            Podemos atualizar esta Política periodicamente para refletir mudanças legais,
            operacionais ou de segurança. A versão vigente estará sempre disponível nesta página,
            com a data da última atualização indicada no topo. Recomendamos revisão periódica.
          </p>
        </Section>

        <footer className="mt-10 pt-6 border-t border-slate-200 text-center">
          <p className="text-xs text-slate-500">
            © {new Date().getFullYear()} Serraria Guedes — CNPJ 20.295.391/0001-26
          </p>
          <div className="mt-3 flex justify-center gap-4 text-xs">
            <Link
              to="/"
              className="text-amber-700 hover:text-amber-900 underline"
              data-testid="privacy-back-home"
            >
              Voltar ao início
            </Link>
          </div>
        </footer>
      </div>
    </div>
  );
}
